import {
  TicketClassificationValue,
  TicketLogReference,
  TicketPriority,
  UploadedTicketFile,
} from './support-ticket-types'
import {brandName} from '../../../../_metronic/helpers'

export type TicketCreatePrefill = {
  subject?: string
  description?: string
  classification?: TicketClassificationValue
  priority?: TicketPriority
  invoiceIds?: number[]
  appLogId?: number
  pageUrl?: string
  draftId?: string
  uploadedFiles?: UploadedTicketFile[]
  taxDiagnosticCode?: string
  taxDiagnosticChecklistConfirmed?: boolean
}

export type TicketCreateRouteState = {
  returnTo?: string
  ticketPrefill?: TicketCreatePrefill
}

type LogTicketContext = {
  id?: number | string | null
  logSectionId?: number
  section?: string
  status?: string
  resultPreview?: string
  result?: string[]
}

type InvoiceTicketContext = {
  id?: number | string | null
  no?: string
}

type WebInvoiceErrorContext = {
  errorMessage?: string
  formLabel?: string
  invoiceTypeLabel?: string
  invoiceNumber?: string
  invoiceId?: number | string | null
  appLogId?: number | string | null
  operation?: 'create' | 'edit'
  pageUrl?: string
}

const fileSectionIds = new Set([7, 8, 9, 15, 16, 17, 20, 21])
const loginSectionIds = new Set([1, 10, 11, 12, 13, 14, 19])
const invoiceSubmissionSectionIds = new Set([3, 6])
const invoiceStatusSectionIds = new Set([4, 5, 18])

const asPositiveInteger = (value: unknown) => {
  const parsed = Number(value)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined
}

export const newTicketRequestId = () => {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20
  )}-${hex.slice(20)}`
}

const cleanText = (value: unknown, maximumLength: number) =>
  typeof value === 'string' ? value.trim().slice(0, maximumLength) : ''

const logTopic = (sectionId?: number) => {
  if (sectionId && loginSectionIds.has(sectionId)) return 'LoginAndAccount'
  if (sectionId && invoiceSubmissionSectionIds.has(sectionId)) return 'InvoiceSubmission'
  if (sectionId && invoiceStatusSectionIds.has(sectionId)) return 'InvoiceStatus'
  if (sectionId && fileSectionIds.has(sectionId)) return 'FileAndAttachment'
  return 'OtherTechnical'
}

const failedStatus = (value?: string) => {
  const normalized = (value || '').trim().toLowerCase()
  return ['failed', 'error', 'ناموفق', 'نا موفق'].includes(normalized)
}

export const ticketPrefillFromLog = (log: LogTicketContext): TicketCreatePrefill => {
  const id = asPositiveInteger(log.id)
  const section = cleanText(log.section, 120) || `عملیات ${brandName}`
  const result =
    cleanText(log.resultPreview, 1200) ||
    cleanText(
      (log.result || []).find((item) => Boolean(item?.trim())),
      1200
    )
  const isError = failedStatus(log.status)
  const subject = `${isError ? 'خطا در' : 'پیگیری'} ${section}${id ? ` (لاگ ${id})` : ''}`

  return {
    subject: subject.slice(0, 254),
    description: [
      `${section}${id ? ` با شماره لاگ ${id}` : ''} نیاز به بررسی پشتیبانی دارد.`,
      result ? `نتیجه ثبت‌شده:\n${result}` : '',
    ]
      .filter(Boolean)
      .join('\n\n'),
    classification: {
      departmentCode: 'TechnicalSupport',
      requestTypeCode: 'Incident',
      topicCode: logTopic(log.logSectionId),
      impactCode: 'SingleItem',
    },
    priority: isError ? 3 : 2,
    appLogId: id,
  }
}

export const ticketPrefillFromInvoiceError = (
  invoice: InvoiceTicketContext,
  errorMessages: string[] = []
): TicketCreatePrefill => {
  const invoiceId = asPositiveInteger(invoice.id)
  const invoiceNumber = cleanText(invoice.no, 120)
  const normalizedErrors = errorMessages
    .map((message) => cleanText(message, 1000))
    .filter(Boolean)
    .slice(0, 5)
  const reference = invoiceNumber
    ? `شماره ${invoiceNumber}`
    : invoiceId
    ? `با شناسه ${invoiceId}`
    : ''

  return {
    subject: `خطای صورتحساب${reference ? ` ${reference}` : ''}`.slice(0, 254),
    description: [
      `صورتحساب${reference ? ` ${reference}` : ''} هنگام پردازش با خطا مواجه شده است.`,
      normalizedErrors.length ? `آخرین خطای ثبت‌شده:\n${normalizedErrors.join('\n')}` : '',
    ]
      .filter(Boolean)
      .join('\n\n'),
    classification: {
      departmentCode: 'TechnicalSupport',
      requestTypeCode: 'Incident',
      topicCode: 'InvoiceSubmission',
      impactCode: 'SingleItem',
    },
    priority: 3,
    invoiceIds: invoiceId ? [invoiceId] : [],
  }
}

export const ticketPrefillFromVatRateDiagnosis = (
  invoice: InvoiceTicketContext,
  draftId: string,
  uploadedFiles: UploadedTicketFile[]
): TicketCreatePrefill => {
  const invoiceId = asPositiveInteger(invoice.id)
  const invoiceNumber = cleanText(invoice.no, 120)
  const reference = invoiceNumber || (invoiceId ? invoiceId.toString() : '')
  return {
    subject: `بررسی مغایرت شناسه کالا یا خدمت${reference ? ` - صورتحساب ${reference}` : ''}`.slice(
      0,
      254
    ),
    description:
      'فایل رسمی سامانه شناسه کالا و خدمت پیوست شده است. لطفاً نتیجه عیب‌یابی ثبت‌شده برای خطای ۰۳۰۴۴۰۱ را بررسی کنید.',
    classification: {
      departmentCode: 'TaxConsulting',
      requestTypeCode: 'ServiceRequest',
      topicCode: 'GoodsAndServicesId',
      impactCode: 'SingleItem',
    },
    priority: 3,
    invoiceIds: invoiceId ? [invoiceId] : [],
    draftId,
    uploadedFiles,
    taxDiagnosticCode: '0304401',
    taxDiagnosticChecklistConfirmed: true,
  }
}

export const ticketPrefillFromWebInvoiceError = ({
  errorMessage,
  formLabel,
  invoiceTypeLabel,
  invoiceNumber,
  invoiceId,
  appLogId,
  operation = 'create',
  pageUrl,
}: WebInvoiceErrorContext): TicketCreatePrefill => {
  const normalizedError = cleanText(errorMessage, 3000)
  const normalizedForm = cleanText(formLabel, 120) || 'فرم وب'
  const normalizedType = cleanText(invoiceTypeLabel, 180)
  const normalizedNumber = cleanText(invoiceNumber, 120)
  const persistedInvoiceId = asPositiveInteger(invoiceId)
  const operationLogId = asPositiveInteger(appLogId)
  const actionLabel = operation === 'edit' ? 'ویرایش' : 'صدور'

  return {
    subject: `خطا در ${actionLabel} صورتحساب از ${normalizedForm}`.slice(0, 254),
    description: [
      `هنگام ${actionLabel} صورتحساب از ${normalizedForm} خطا رخ داده است.`,
      normalizedType ? `نوع صورتحساب: ${normalizedType}` : '',
      normalizedNumber ? `شماره واردشده: ${normalizedNumber}` : '',
      normalizedError ? `شرح خطا:\n${normalizedError}` : '',
    ]
      .filter(Boolean)
      .join('\n\n'),
    classification: {
      departmentCode: 'TechnicalSupport',
      requestTypeCode: 'Incident',
      topicCode: 'InvoiceSubmission',
      impactCode: 'SingleItem',
    },
    priority: 3,
    invoiceIds: persistedInvoiceId ? [persistedInvoiceId] : [],
    appLogId: operationLogId,
    pageUrl: cleanText(pageUrl, 2048),
  }
}

export const ticketPrefillFromReference = (source: TicketLogReference): TicketCreatePrefill =>
  ticketPrefillFromLog(source)
